/* ═══════════════════════════════════════════════════════════════════════════
   AN EVENING AT ONE TABLE

   The guest scans the card on the table and orders for the rest of the
   evening themselves. What arrives goes to the kitchen and bar screens at
   once, so a refill does not require catching a waiter's eye, and the bill
   accumulates — one for the table, or one each by what everybody actually
   ordered — and is settled once rather than at five card terminals.

   ── The code IS the table ─────────────────────────────────────────────────

   Scanning is not "choosing table 14". It is standing at table 14, which the
   restaurant can see. So there is no table picker at check-in either, and
   nobody — guest or platform — ever types a table number. Same rule as
   domain/tables.js, arrived at from the other end.

   ── The first guest sets the table, not the platform ──────────────────────

   Whoever scans first chooses one bill or separate ones, and everybody who
   scans afterwards joins that. It is a decision about the evening and the
   people at it, and neither the restaurant nor we know enough to make it.

   ── Money is reserved, not taken ──────────────────────────────────────────

   Each order places a hold and nothing is captured until the table settles.
   That is how holds work at Vipps and on the card networks, and it is also
   what lets the table keep ordering without paying again every round.

   ── And alcohol ───────────────────────────────────────────────────────────

   Every alcoholic order waits for a waiter, whatever the app knows about the
   guest's age. See domain/menu.js — it is the condition on which a thing
   like this may be used at a licensed premises at all.
   ═══════════════════════════════════════════════════════════════════════ */

import { limitOf, needsWaiter, mayOrder } from './menu.js';

/** Where an order is. In order, because the screen draws them in it. */
export const STATUSES = {
  waiter: { label: 'Waiting for a waiter', short: 'waiter' },
  taken: { label: 'Taken', short: 'taken' },
  making: { label: 'Being made', short: 'making' },
  served: { label: 'Served', short: 'served' },
};

export const STATUS_ORDER = ['waiter', 'taken', 'making', 'served'];

/** The colours a table's guests are told apart by. Six, because a table of
    more than six is two tables pushed together and gets its own session. */
export const COLOURS = ['#c4a06d', '#2d5599', '#2f8b55', '#a3452f', '#6b4f9e', '#0f7a86'];

/** A table's session key. One per table per place — the code on the table is
    the only thing that identifies it. */
export const keyOf = (businessId, table) => `${businessId}#${table}`;

/** A session that has not started yet. `mode` is the first guest's to set. */
export const emptySession = (businessId, table) => ({
  key: keyOf(businessId, table),
  businessId,
  table: Number(table),
  mode: 'separate',              /* 'separate' | 'together' */
  guests: [],
  orders: [],
  seq: 0,
  opened: null,
  settled: null,
});

export const MODES = {
  separate: { id: 'separate', label: 'Each their own', what: 'You pay only for what you order.' },
  together: { id: 'together', label: 'One bill', what: 'Everything on the table goes on one bill.' },
};

/* ── Who is at the table ──────────────────────────────────────────────── */

/**
 * Add a guest.
 *
 * `age` is `{ over18, over20 }` as a verification answered it, or null. It is
 * never a date of birth: the platform keeps the answer and not the date, and
 * keeps it only for as long as the session lasts.
 */
export function join(session, { name, age = null, by = null, phone = null } = {}) {
  const seq = session.seq + 1;
  const guest = {
    id: 'g' + seq,
    name: String(name || '').trim() || `Guest ${session.guests.length + 1}`,
    colour: COLOURS[session.guests.length % COLOURS.length],
    age: age ? { over18: !!age.over18, over20: !!age.over20, by } : null,
    /* The number this guest is known by, when there is one. It is how the
       receipt reaches them afterwards — and it is the only reason it is
       here, so a guest who gives none simply gets no receipt rather than
       being asked for an email address at the end of a meal. */
    phone: phone || null,
    joined: new Date().toISOString(),
  };
  return {
    ...session,
    seq,
    guests: [...session.guests, guest],
    opened: session.opened || guest.joined,
  };
}

export const guestIn = (session, id) => session.guests.find(g => g.id === id) || null;

/** The first guest decides. Afterwards it is the table's and not one
    person's, so it stops being changeable the moment anybody else joins. */
export const mayChooseMode = session => session.guests.length <= 1;

/* ── Ordering ─────────────────────────────────────────────────────────── */

/**
 * What would happen if this basket were sent.
 *
 * Answered before anything is written, so the screen can say it rather than
 * discovering it afterwards. `blocked` is the lines this guest may not place
 * at all; `waiter` is whether a person has to approve it.
 */
export function check(guest, lines = []) {
  const blocked = lines.filter(l => l.alc && !mayOrder(guest, l.alc));
  return {
    blocked,
    ok: lines.length > 0 && blocked.length === 0,
    waiter: needsWaiter(lines),
    limit: limitOf(lines),
    total: lines.reduce((a, l) => a + l.price * (l.qty || 1), 0),
  };
}

/**
 * Place an order.
 *
 * Throws rather than silently dropping a line: a basket that came back
 * shorter than it went in is the worst of the three possible behaviours.
 */
export function order(session, guestId, lines = [], now = new Date()) {
  const guest = guestIn(session, guestId);
  if (!guest) throw new Error('That guest is not checked in at this table.');
  if (!lines.length) throw new Error('Nothing in the order.');
  const seen = check(guest, lines);
  if (seen.blocked.length) {
    throw new Error(seen.blocked.length === 1
      ? `${seen.blocked[0].name} is over ${seen.blocked[0].alc} only.`
      : `${seen.blocked.length} of those are over 18 only.`);
  }

  const seq = session.seq + 1;
  const row = {
    id: 'o' + seq,
    guestId,
    at: now.toISOString(),
    /* Every alcoholic order waits for a person, whatever the app knows about
       the age. The licence asks the place to judge more than age and an app
       cannot — see domain/menu.js. */
    status: seen.waiter ? 'waiter' : 'taken',
    limit: seen.limit,
    lines: lines.map(l => ({
      id: l.id, name: l.name, price: l.price, qty: l.qty || 1, alc: l.alc || 0,
      /* Food or drink, carried onto the order rather than looked up again
         later. It decides the VAT rate on the receipt — 15 and 25 — and a
         bill that has to re-derive it from a menu that may have changed by
         then is a bill that eventually gets it wrong. */
      kind: l.kind === 'drink' ? 'drink' : 'food',
    })),
    total: seen.total,
    /* The hold. Reserved now, captured when the table settles — which is what
       lets the table keep ordering without paying again every round. */
    hold: { state: 'reserved', amount: seen.total, demo: true },
  };
  return { ...session, seq, orders: [...session.orders, row] };
}

/** Move an order along. The waiter's approval is just the first step of the
    same line, which is why there is one function and not two. */
export function advance(session, orderId, to) {
  if (!STATUS_ORDER.includes(to)) throw new Error('Unknown status.');
  return {
    ...session,
    orders: session.orders.map(o => (o.id === orderId ? { ...o, status: to } : o)),
  };
}

/** What the kitchen and the bar are waiting on, oldest first. */
export const waiting = session =>
  session.orders.filter(o => o.status !== 'served')
    .sort((a, b) => String(a.at).localeCompare(String(b.at)));

/** How many orders need a person before the bar may pour them. */
export const needingWaiter = session =>
  session.orders.filter(o => o.status === 'waiter').length;

/* ── The bill ─────────────────────────────────────────────────────────── */

/** What one guest has ordered, or the whole table when `guestId` is null. */
export const totalOf = (session, guestId = null) =>
  session.orders
    .filter(o => guestId == null || o.guestId === guestId)
    .reduce((a, o) => a + o.total, 0);

/** The lines so far, added up per dish — a bill, not a log. */
export function billLines(session, guestId = null) {
  const out = [];
  session.orders
    .filter(o => guestId == null || o.guestId === guestId)
    .forEach(o => o.lines.forEach(l => {
      const had = out.find(x => x.id === l.id);
      if (had) had.qty += l.qty;
      else out.push({ ...l });
    }));
  return out;
}

/**
 * How a bill divides.
 *
 * `mode` 'spent' — everybody owes what they ordered.
 * `mode` 'even' — the total split between the people included.
 *
 * `withWhom` is who gets a request. Leaving somebody out is a real thing and
 * not an edge case: if you are treating one of them, no request should go to
 * her — and whatever is left over lands on the person paying, because
 * somebody has to owe it and they are the one who said so.
 */
export function split(session, payerId, { mode = 'spent', withWhom = [] } = {}) {
  const total = totalOf(session);
  const included = session.guests.filter(g => g.id === payerId || withWhom.includes(g.id));
  if (!included.length) return { total, parts: [] };

  if (mode === 'even') {
    const each = Math.round(total / included.length);
    return {
      total,
      parts: included.map(g => ({
        guest: g,
        /* The rounding lands on the payer rather than being lost. Four ways
           of 333 is 999, and the missing krone is theirs. */
        amount: g.id === payerId ? total - each * (included.length - 1) : each,
      })),
    };
  }

  const parts = included.map(g => ({ guest: g, amount: totalOf(session, g.id) }));
  const covered = parts.reduce((a, p) => a + p.amount, 0);
  parts.forEach(p => { if (p.guest.id === payerId) p.amount += total - covered; });
  return { total, parts };
}

/** Settle. The holds are captured and the table is closed; the orders stay on
    the record, because a bill nobody can look at afterwards is a receipt that
    was never issued. */
export function settle(session, now = new Date()) {
  return {
    ...session,
    settled: now.toISOString(),
    orders: session.orders.map(o => ({
      ...o,
      status: 'served',
      hold: { ...o.hold, state: 'captured', charged: o.total },
    })),
  };
}

export const isOpen = session => !!session.opened && !session.settled;

/** What a request to another guest actually is, said in the place somebody
    would otherwise assume it is a transfer. */
export const requestWords = () =>
  'They get a Vipps request with the table and the place on it. If they do '
  + 'not pay it, the amount stays on you — a request asks, it does not take.';
